import { type BillingPlanDto, REMINDERS_PAUSED_REASONS, resolveRemindersPausedReason } from '@shared/core/billing'
import { useCallback, useState } from 'react'
import { useBulkUpdateCalendars } from 'src/modules/calendar/hooks/use-bulk-update-calendars'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import { isCalendarLimitExceeded } from 'src/modules/calendar/utils/helpers'
import { useChangeSubscription } from 'src/modules/payment/hooks/use-change-subscription'
import { useStartShopifyCheckout } from 'src/modules/payment/hooks/use-shopify-billing'
import { useSubscription } from 'src/modules/payment/hooks/use-subscription'
import { useUpdateSubscriptionPreview } from 'src/modules/payment/hooks/use-update-subscription-preview'
import { SubscriptionStatus } from 'src/modules/payment/types/payment.types'
import type { PlanChangeStatus } from 'src/modules/payment/types/plans-offer.types'
import { canShopifyUserChangePlanDuringGrace, isShopifyBilledProfile } from 'src/modules/payment/utils/helpers'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import type { UpdateCalendarBodyDto } from 'src/types/calendar.dto'

/** A subscriber's plan change: error modals, calendar selection, proration confirmation, then the update. */
export function usePlanChangeFlow(isYearly: boolean) {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [isCalendarSelectionModalOpen, setIsCalendarSelectionModalOpen] = useState(false)
  const [newPlan, setNewPlan] = useState<BillingPlanDto>(null)
  const [status, setStatus] = useState<PlanChangeStatus>(null)
  const profile = useProfile()
  const isShopifyBilled = isShopifyBilledProfile(profile.data)
  const updateSubscriptionPreview = useUpdateSubscriptionPreview(newPlan?.paddle_id, {
    // Shopify shows proration math on its own confirmation screen.
    enabled: isConfirmModalOpen && !isShopifyBilled,
  })
  const bulkUpdateCalendars = useBulkUpdateCalendars()
  const changeSubscription = useChangeSubscription()
  const startShopifyCheckout = useStartShopifyCheckout()
  const state = { setNewPlan, setStatus, setIsCalendarSelectionModalOpen, setIsConfirmModalOpen }
  const startPlanChange = useStartPlanChange(state)

  const onSelectCalendars = (updatedCalendars: UpdateCalendarBodyDto[]) => {
    setIsCalendarSelectionModalOpen(false)
    bulkUpdateCalendars.mutate(updatedCalendars, {
      onSuccess: () => setIsConfirmModalOpen(true),
    })
  }

  const handleChangeSubscription = () =>
    isShopifyBilled
      ? startShopifyPlanChange({ newPlan, isYearly, startShopifyCheckout, ...state })
      : changePaddlePlan({ newPlan, changeSubscription, ...state })

  return {
    newPlan,
    status,
    isShopifyBilled,
    isConfirmModalOpen,
    isCalendarSelectionModalOpen,
    immediatePaymentAmount: updateSubscriptionPreview.data?.immediate_payment.amount,
    isLoading: updateSubscriptionPreview.isLoading || changeSubscription.isLoading || bulkUpdateCalendars.isLoading,
    startPlanChange,
    onSelectCalendars,
    handleChangeSubscription,
    closeConfirmModal: () => setIsConfirmModalOpen(false),
    closeCalendarSelectionModal: () => setIsCalendarSelectionModalOpen(false),
    closeStatus: () => setStatus(null),
  }
}

export type PlanChangeFlow = ReturnType<typeof usePlanChangeFlow>

type PlanChangeState = {
  setNewPlan: (plan: BillingPlanDto) => void
  setStatus: (status: PlanChangeStatus) => void
  setIsCalendarSelectionModalOpen: (isOpen: boolean) => void
  setIsConfirmModalOpen: (isOpen: boolean) => void
}

function useStartPlanChange(state: PlanChangeState) {
  const { setNewPlan, setStatus, setIsCalendarSelectionModalOpen, setIsConfirmModalOpen } = state
  const calendars = useCalendars()
  const profile = useProfile()
  const isShopifyBilled = isShopifyBilledProfile(profile.data)
  // Shopify-billed users have no Paddle `billing_subscription_id`, but the backend synthesizes a subscription DTO.
  const subscription = useSubscription({}, { enabled: !!profile.data?.billing_subscription_id || isShopifyBilled })
  const subscriptionState = subscription.data?.state
  // Keyed on the reason, not the state: Shopify subscriptions are synthesized as `paused` too.
  const pausedReason = profile.data ? resolveRemindersPausedReason(profile.data) : null

  return useCallback(
    (plan: BillingPlanDto) => {
      const defaultSelectedCalendars = (calendars.data || []).filter((calendar) => calendar.selected)

      if (subscriptionState === SubscriptionStatus.trialing) {
        setStatus('trialError')
      } else if (
        subscriptionState === SubscriptionStatus.deleted &&
        !canShopifyUserChangePlanDuringGrace(profile.data, subscriptionState)
      ) {
        setStatus('canceledError')
      } else if (subscriptionState === SubscriptionStatus.past_due) {
        setStatus('pastDueError')
      } else if (pausedReason === REMINDERS_PAUSED_REASONS.paymentPaused) {
        setStatus('pausedError')
      } else {
        // Also handles a subscriber whose current plan is no longer for sale.
        setNewPlan(plan)
        if (isCalendarLimitExceeded(plan, defaultSelectedCalendars)) {
          setIsCalendarSelectionModalOpen(true)
        } else {
          setIsConfirmModalOpen(true)
        }
      }
    },
    [
      calendars.data,
      profile.data,
      subscriptionState,
      pausedReason,
      setNewPlan,
      setStatus,
      setIsCalendarSelectionModalOpen,
      setIsConfirmModalOpen,
    ],
  )
}

type ShopifyPlanChangeOptions = PlanChangeState & {
  newPlan: BillingPlanDto
  isYearly: boolean
  startShopifyCheckout: ReturnType<typeof useStartShopifyCheckout>
}

function startShopifyPlanChange({ newPlan, isYearly, startShopifyCheckout, ...state }: ShopifyPlanChangeOptions) {
  // Shopify Billing requires admin context for `appSubscriptionCreate`, so the merchant
  // approves the change in the embedded app rather than through Paddle.
  startShopifyCheckout.mutate(
    { paddle_plan_id: newPlan.paddle_id, is_yearly: isYearly },
    {
      onSuccess: ({ shopify_admin_url }) => {
        state.setIsConfirmModalOpen(false)
        window.top?.location.assign(shopify_admin_url)
      },
      onError: () => {
        state.setStatus('error')
        state.setIsConfirmModalOpen(false)
      },
    },
  )
}

type PaddlePlanChangeOptions = PlanChangeState & {
  newPlan: BillingPlanDto
  changeSubscription: ReturnType<typeof useChangeSubscription>
}

function changePaddlePlan({ newPlan, changeSubscription, ...state }: PaddlePlanChangeOptions) {
  changeSubscription.mutate(
    { plan_id: newPlan.paddle_id.toString(), bill_immediately: true },
    {
      onSuccess: () => state.setStatus('success'),
      onError: () => state.setStatus('error'),
      onSettled: () => state.setIsConfirmModalOpen(false),
    },
  )
}
